import { compact, isEmpty, map, partition, reduce } from "lodash/fp";
import { DateTime } from "luxon";

import {
  ApiProductivityIntegrationStatus,
  ProductivityIntegrationStatus,
  ProductivityIntegrationStatusStep,
} from "@readai/common/types";
import type { ApiWebhook, Webhook } from "@readai/common/types/webhookTypes";
import type {
  ApiZapierIntegration,
  ZapierIntegration,
} from "@readai/common/types/zapierTypes";
import {
  optionalParseUTCDateTimeFromString,
  parseUTCDateTimeFromString,
} from "@readai/common/utils/datetimeUtils";

import { API_PRODUCTIVITY_CHANNEL_PROVIDER_TO_PROVIDER } from "./topics";

export const parseWebhooks = (data: ApiWebhook[]): Webhook[] =>
  data.map((d) => ({
    ...d,
    createdOn: parseUTCDateTimeFromString(d.createdOn),
    updatedOn: parseUTCDateTimeFromString(d.updatedOn),
    lastRequestOn: optionalParseUTCDateTimeFromString(d.lastRequestOn),
  }));

export const parseZapierApiKeys = (
  data: ApiZapierIntegration[],
): ZapierIntegration[] =>
  data.map((d) => ({
    ...d,
    createdOn: parseUTCDateTimeFromString(d.createdOn),
    lastUsed: optionalParseUTCDateTimeFromString(d.lastUsed),
  }));

const integrationStatusHasError = (
  status: ApiProductivityIntegrationStatus,
) => {
  return (
    isEmpty(status.topicReportIds) &&
    // a null values means we don't know if there was an error, so it's
    // only an error if it's defined and false
    status.firstRunSucceeded != null &&
    !status.firstRunSucceeded
  );
};

const deriveStep = (status: ApiProductivityIntegrationStatus) => {
  if (!isEmpty(status.topicReportIds) || integrationStatusHasError(status)) {
    // a provider has finished processing if:
    // 1) at least one Readout has been generated OR
    // 2) we hit an error while initially processing
    return ProductivityIntegrationStatusStep.COMPLETE;
  }

  if (status.latestLastSyncTime != null) {
    const hasTakeaways =
      status.actionItemCount != null && status.keyQuestionCount != null;
    return hasTakeaways
      ? ProductivityIntegrationStatusStep.REPORT_GENERATION
      : ProductivityIntegrationStatusStep.INGESTION;
  }

  return ProductivityIntegrationStatusStep.INITIAL;
};

const parseIntegrationStatus = (
  data: ApiProductivityIntegrationStatus,
): ProductivityIntegrationStatus => ({
  provider: API_PRODUCTIVITY_CHANNEL_PROVIDER_TO_PROVIDER[data.source],
  lastSyncTime: optionalParseUTCDateTimeFromString(data.latestLastSyncTime),
  numChannels: data.channelCount ?? null,
  numMessages: data.messageCount ?? null,
  numActionItems: data.actionItemCount ?? null,
  numKeyQuestions: data.keyQuestionCount ?? null,
  topicReportIds: data.topicReportIds ?? [],
  createdOn: optionalParseUTCDateTimeFromString(data.userAccessCreation),
  step: deriveStep(data),
  error: integrationStatusHasError(data),
});

const addOrNull = (
  value1: number | null,
  value2: number | null,
): number | null => {
  if (value1 == null && value2 == null) return null;
  return (value1 ?? 0) + (value2 ?? 0);
};

/**
 * Combines multiple integration statuses into a single object. Currently only
 * used to combine the two teams sources (teams_chat and teams_channel), since
 * we don't expose the distinction between those two channels to the user
 * outside of permission issues (only Teams admins can authorize channel access).
 * */
const combineIntegrationStatuses = (
  data: ApiProductivityIntegrationStatus[],
): ProductivityIntegrationStatus | null => {
  if (isEmpty(data)) return null;

  const parsedIntegrations = map(parseIntegrationStatus, data);

  return reduce(
    (combinedStatus, status) => {
      if (isEmpty(combinedStatus)) return status;
      return {
        provider: combinedStatus.provider,
        lastSyncTime: combinedStatus.lastSyncTime
          ? combinedStatus.lastSyncTime
          : status.lastSyncTime,
        numChannels: addOrNull(combinedStatus.numChannels, status.numChannels),
        numMessages: addOrNull(combinedStatus.numMessages, status.numMessages),
        numActionItems: addOrNull(
          combinedStatus.numActionItems,
          status.numActionItems,
        ),
        numKeyQuestions: addOrNull(
          combinedStatus.numKeyQuestions,
          status.numKeyQuestions,
        ),
        topicReportIds: [
          ...combinedStatus.topicReportIds,
          ...status.topicReportIds,
        ],
        createdOn: DateTime.min(
          ...compact([combinedStatus.createdOn, status.createdOn]),
        ),
        step: Math.max(combinedStatus.step, status.step),
        error: combinedStatus.error || status.error,
      };
    },
    {} as ProductivityIntegrationStatus, // will get immediately overwritten
    parsedIntegrations,
  );
};

export const parseIntegrationStatuses = (
  data: ApiProductivityIntegrationStatus[],
): ProductivityIntegrationStatus[] => {
  const [teamsIntegrations, nonTeamsIntegrations] = partition(
    (d) =>
      API_PRODUCTIVITY_CHANNEL_PROVIDER_TO_PROVIDER[d.source] ===
      "microsoft_graph_teams_chat_ingest",
    data,
  );

  const combinedTeamsIntegrationStatus =
    combineIntegrationStatuses(teamsIntegrations);

  const parsedOtherIntegrations = map(
    parseIntegrationStatus,
    nonTeamsIntegrations,
  );

  return compact([...parsedOtherIntegrations, combinedTeamsIntegrationStatus]);
};
